/**
 * 监控页面
 * Feature: vanadium-industry-map
 * 验证需求：11.5
 * 
 * 功能：
 * - 显示数据源健康状态
 * - 显示最近采集时间
 * - 显示数据覆盖率
 * - 显示数据质量报告
 * - 系统整体健康状态
 */
import React, { useState } from 'react';
import {
  Row,
  Col,
  Badge,
  Table,
  Tag,
  Space,
  Alert,
  Descriptions,
  Progress,
  Tabs,
  Select,
  Button,
  message,
  Statistic,
} from 'antd';
import {
  CheckCircleOutlined,
  WarningOutlined,
  CloseCircleOutlined,
  QuestionCircleOutlined,
  SyncOutlined,
  ThunderboltOutlined,
} from '@ant-design/icons';
import { LoadingIndicator, ErrorAlert } from '../components';
import {
  useDataSourcesStatus,
  useQualitySummary,
  useSystemHealth,
  useQualityReports,
} from '../hooks/useMonitoringData';
import type { DataSourceStatus } from '../api/monitoring';
import './Monitoring.scss';

const { TabPane } = Tabs;
const { Option } = Select;

/**
 * 健康状态图标映射
 */
const getHealthIcon = (status: string) => {
  switch (status) {
    case 'healthy':
      return <CheckCircleOutlined style={{ color: '#52c41a', fontSize: '24px' }} />;
    case 'warning':
      return <WarningOutlined style={{ color: '#faad14', fontSize: '24px' }} />;
    case 'critical':
      return <CloseCircleOutlined style={{ color: '#ff4d4f', fontSize: '24px' }} />;
    default:
      return <QuestionCircleOutlined style={{ color: '#d9d9d9', fontSize: '24px' }} />;
  }
};

/**
 * 健康状态Badge映射
 */
const getHealthBadge = (status: string) => {
  switch (status) {
    case 'healthy':
      return <Badge status="success" text="健康" />;
    case 'warning':
      return <Badge status="warning" text="警告" />;
    case 'critical':
      return <Badge status="error" text="严重" />;
    default:
      return <Badge status="default" text="未知" />;
  }
};

/**
 * 数据源卡片组件
 */
const DataSourceCard: React.FC<{ title: string; data: DataSourceStatus }> = ({ title, data }) => {
  return (
    <div className="glass-card datasource-card">
      <div className="card-title">
        <Space>
          {getHealthIcon(data.health_status)}
          <span>{title}</span>
        </Space>
        {getHealthBadge(data.health_status)}
      </div>
      <Descriptions column={1} size="small">
        <Descriptions.Item label="数据源">{data.source}</Descriptions.Item>
        <Descriptions.Item label="最近采集">
          {data.last_collected
            ? new Date(data.last_collected).toLocaleString('zh-CN')
            : '暂无数据'}
        </Descriptions.Item>
        <Descriptions.Item label="总记录数">
          {data.total_records.toLocaleString()}
        </Descriptions.Item>
        <Descriptions.Item label="更新频率">{data.expected_update_frequency}</Descriptions.Item>
      </Descriptions>

      {/* 数据覆盖率 */}
      <div style={{ marginTop: '16px' }}>
        <div style={{ marginBottom: '8px', fontWeight: 'bold' }}>数据覆盖率：</div>
        {Object.entries(data.coverage).map(([key, value]) => (
          <div key={key} style={{ marginBottom: '4px' }}>
            <Tag color="blue">{key}</Tag>
            <span>{typeof value === 'number' ? value.toLocaleString() : value}</span>
          </div>
        ))}
      </div>
    </div>
  );
};

const Monitoring: React.FC = () => {
  const [qualityDays, setQualityDays] = useState(7);
  const [activeTab, setActiveTab] = useState('overview');
  const [generating, setGenerating] = useState(false);

  // 获取数据
  const { data: systemHealth, isLoading: healthLoading, error: healthError } = useSystemHealth();
  const { data: dataSources, isLoading: sourcesLoading, error: sourcesError, refetch: refetchSources } = useDataSourcesStatus();
  const { data: qualitySummary, isLoading: qualityLoading, error: qualityError, refetch: refetchQuality } = useQualitySummary(qualityDays);
  const { data: qualityReports, isLoading: reportsLoading, refetch: refetchReports } = useQualityReports({ limit: 20 });

  /**
   * 生成测试数据
   */
  const handleGenerateTestData = async () => {
    setGenerating(true);
    const hideLoading = message.loading('正在生成测试数据...', 0);

    try {
      const response = await fetch('/api/v1/scheduler/test-collection/all', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();

      hideLoading();

      if (result.success) {
        const { price, storage, production } = result.collections;

        // 统计成功的采集
        const successCount = [price, storage, production].filter(c => c.success).length;

        if (successCount === 3) {
          message.success({
            content: (
              <div>
                <div>✅ 测试数据生成成功!</div>
                <div style={{ fontSize: '12px', marginTop: '4px' }}>
                  价格: {price.saved}条 | 储能: {storage.saved}个 | 产量: {production.saved}条
                </div>
              </div>
            ),
            duration: 5,
          });
        } else {
          message.warning({
            content: `部分数据生成成功 (${successCount}/3)`,
            duration: 5,
          });
        }

        // 刷新数据
        setTimeout(() => {
          refetchSources();
          refetchQuality();
          refetchReports();
        }, 1000);
      } else {
        message.error('测试数据生成失败');
      }
    } catch (error) {
      hideLoading();
      console.error('生成测试数据失败:', error);
      message.error({
        content: '生成测试数据失败: ' + (error as Error).message,
        duration: 5,
      });
    } finally {
      setGenerating(false);
    }
  };

  if (healthLoading || sourcesLoading) {
    return <LoadingIndicator loading={true} tip="加载监控数据..." />;
  }

  if (healthError || sourcesError) {
    return (
      <ErrorAlert
        message="加载失败"
        description="无法加载监控数据，请稍后重试"
      />
    );
  }

  // 质量报告表格列定义
  const qualityReportColumns = [
    {
      title: '数据类型',
      dataIndex: 'data_type',
      key: 'data_type',
      render: (text: string) => <Tag color="blue">{text}</Tag>,
    },
    {
      title: '检查时间',
      dataIndex: 'check_time',
      key: 'check_time',
      render: (text: string) => new Date(text).toLocaleString('zh-CN'),
    },
    {
      title: '质量分数',
      dataIndex: 'quality_score',
      key: 'quality_score',
      render: (score: number) => (
        <Progress
          percent={score}
          size="small"
          status={score >= 80 ? 'success' : score >= 60 ? 'normal' : 'exception'}
        />
      ),
    },
    {
      title: '总记录数',
      dataIndex: 'total_records',
      key: 'total_records',
      render: (num: number) => num.toLocaleString(),
    },
    {
      title: '有效记录',
      dataIndex: 'valid_records',
      key: 'valid_records',
      render: (num: number) => num.toLocaleString(),
    },
    {
      title: '无效记录',
      dataIndex: 'invalid_records',
      key: 'invalid_records',
      render: (num: number) => (
        <span style={{ color: num > 0 ? '#ff4d4f' : '#52c41a' }}>
          {num.toLocaleString()}
        </span>
      ),
    },
  ];

  return (
    <div className="monitoring-page">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
        <h1 style={{ margin: 0 }}>系统监控</h1>
        <Button
          type="primary"
          icon={<ThunderboltOutlined />}
          onClick={handleGenerateTestData}
          loading={generating}
          size="large"
        >
          生成测试数据
        </Button>
      </div>

      {/* 系统整体健康状态 */}
      {systemHealth && (
        <Alert
          message={
            <Space>
              <span style={{ fontSize: '16px', fontWeight: 'bold' }}>系统状态</span>
              {getHealthBadge(systemHealth.overall_health)}
            </Space>
          }
          description={
            <div>
              <div>更新时间：{new Date(systemHealth.timestamp).toLocaleString('zh-CN')}</div>
              <div style={{ marginTop: '8px' }}>
                {systemHealth.recommendations.map((rec, index) => (
                  <div key={index}>• {rec}</div>
                ))}
              </div>
            </div>
          }
          type={
            systemHealth.overall_health === 'healthy'
              ? 'success'
              : systemHealth.overall_health === 'warning'
                ? 'warning'
                : 'error'
          }
          showIcon
          icon={getHealthIcon(systemHealth.overall_health)}
          style={{ marginBottom: '24px' }}
        />
      )}

      <Tabs activeKey={activeTab} onChange={setActiveTab}>
        {/* 概览标签页 */}
        <TabPane tab="概览" key="overview">
          {/* 数据源状态卡片 */}
          {dataSources && (
            <>
              <h2 style={{ marginBottom: '16px' }}>数据源健康状态</h2>
              <Row gutter={[16, 16]} style={{ marginBottom: '24px' }}>
                <Col xs={24} md={8}>
                  <DataSourceCard title="价格数据" data={dataSources.price_data} />
                </Col>
                <Col xs={24} md={8}>
                  <DataSourceCard title="储能项目" data={dataSources.storage_projects} />
                </Col>
                <Col xs={24} md={8}>
                  <DataSourceCard title="产量数据" data={dataSources.production_data} />
                </Col>
              </Row>
            </>
          )}

          {/* 数据质量汇总 */}
          {qualitySummary && (
            <>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
                <h2 style={{ margin: 0 }}>数据质量汇总</h2>
                <Space>
                  <span>统计周期：</span>
                  <Select value={qualityDays} onChange={setQualityDays} style={{ width: 120 }}>
                    <Option value={7}>最近7天</Option>
                    <Option value={14}>最近14天</Option>
                    <Option value={30}>最近30天</Option>
                  </Select>
                </Space>
              </div>

              <Row gutter={[16, 16]} style={{ marginBottom: '24px' }}>
                <Col xs={24} md={8}>
                  <div className="stat-card">
                    <div className="stat-title">总体平均分数</div>
                    <div className="stat-value" style={{
                      color:
                        qualitySummary.overall.status === 'good'
                          ? '#52c41a'
                          : qualitySummary.overall.status === 'warning'
                            ? '#faad14'
                            : '#ff4d4f',
                    }}>
                      {qualitySummary.overall.average_score.toFixed(1)}
                    </div>
                    <div className="stat-suffix">/ 100</div>
                    <div style={{ marginTop: '8px' }}>
                      {getHealthBadge(
                        qualitySummary.overall.status === 'good'
                          ? 'healthy'
                          : qualitySummary.overall.status === 'warning'
                            ? 'warning'
                            : 'critical'
                      )}
                    </div>
                  </div>
                </Col>

                {qualitySummary.by_data_type.map((item) => (
                  <Col xs={24} md={8} key={item.data_type}>
                    <div className="stat-card">
                      <div className="stat-title">{item.data_type}</div>
                      <div className="stat-value">{item.average_score.toFixed(1)}</div>
                      <div className="stat-suffix">/ 100</div>
                      <div className="stat-details">
                        <div>最高：{item.max_score.toFixed(1)}</div>
                        <div>最低：{item.min_score.toFixed(1)}</div>
                        <div>报告数：{item.report_count}</div>
                      </div>
                    </div>
                  </Col>
                ))}
              </Row>

              {/* 最近问题 */}
              {qualitySummary.recent_issues.length > 0 && (
                <div className="glass-card">
                  <div className="card-title">最近问题</div>
                  {qualitySummary.recent_issues.map((issue, index) => (
                    <Alert
                      key={index}
                      className="issue-alert"
                      message={
                        <Space>
                          <Tag color="red">{issue.data_type}</Tag>
                          <span>质量分数：{issue.quality_score.toFixed(1)}</span>
                          <span style={{ color: 'rgba(255,255,255,0.5)', fontSize: '12px' }}>
                            {new Date(issue.check_time).toLocaleString('zh-CN')}
                          </span>
                        </Space>
                      }
                      description={
                        <ul style={{ margin: 0, paddingLeft: '20px' }}>
                          {issue.issues.map((iss, idx) => (
                            <li key={idx}>{iss}</li>
                          ))}
                        </ul>
                      }
                      type="warning"
                    />
                  ))}
                </div>
              )}
            </>
          )}
        </TabPane>

        {/* 质量报告标签页 */}
        <TabPane tab="质量报告" key="reports">
          <div className="glass-card">
            <Table
              columns={qualityReportColumns}
              dataSource={qualityReports?.reports || []}
              rowKey="id"
              loading={reportsLoading}
              pagination={{
                total: qualityReports?.total || 0,
                pageSize: 20,
                showSizeChanger: false,
                showTotal: (total) => `共 ${total} 条记录`,
              }}
              expandable={{
                expandedRowRender: (record) => (
                  <div style={{ padding: '16px' }}>
                    <Row gutter={16}>
                      <Col span={12}>
                        <h4>问题列表：</h4>
                        {record.issues.length > 0 ? (
                          <ul>
                            {record.issues.map((issue, idx) => (
                              <li key={idx}>{issue}</li>
                            ))}
                          </ul>
                        ) : (
                          <div style={{ color: '#999' }}>无问题</div>
                        )}
                      </Col>
                      <Col span={12}>
                        <h4>建议：</h4>
                        {record.recommendations.length > 0 ? (
                          <ul>
                            {record.recommendations.map((rec, idx) => (
                              <li key={idx}>{rec}</li>
                            ))}
                          </ul>
                        ) : (
                          <div style={{ color: '#999' }}>无建议</div>
                        )}
                      </Col>
                    </Row>
                    <Row gutter={16} style={{ marginTop: '16px' }}>
                      <Col span={8}>
                        <Statistic
                          title="完整性分数"
                          value={record.completeness_score}
                          precision={1}
                          suffix="/ 100"
                        />
                      </Col>
                      <Col span={8}>
                        <Statistic
                          title="准确性分数"
                          value={record.accuracy_score}
                          precision={1}
                          suffix="/ 100"
                        />
                      </Col>
                      <Col span={8}>
                        <Statistic
                          title="总体质量分数"
                          value={record.quality_score}
                          precision={1}
                          suffix="/ 100"
                        />
                      </Col>
                    </Row>
                  </div>
                ),
              }}
            />
          </div>
        </TabPane>
      </Tabs>

      {/* 说明 */}
      <div className="glass-card info-card" style={{ marginTop: '24px' }}>
        <div className="card-title">监控说明</div>
        <Space direction="vertical" size="middle">
          <div>
            <strong>健康状态：</strong>
            <ul style={{ marginTop: '8px' }}>
              <li>
                <Badge status="success" /> 健康：数据源在预期时间内更新
              </li>
              <li>
                <Badge status="warning" /> 警告：数据源更新延迟，但在可接受范围内
              </li>
              <li>
                <Badge status="error" /> 严重：数据源长时间未更新，需要检查
              </li>
              <li>
                <Badge status="default" /> 未知：无法获取数据源状态
              </li>
            </ul>
          </div>
          <div>
            <strong>更新频率：</strong>
            <ul style={{ marginTop: '8px' }}>
              <li>价格数据：每日更新</li>
              <li>储能项目：每周更新</li>
              <li>产量数据：每月更新</li>
            </ul>
          </div>
          <div>
            <strong>质量分数：</strong>
            <ul style={{ marginTop: '8px' }}>
              <li>80-100分：良好</li>
              <li>60-79分：警告</li>
              <li>0-59分：严重</li>
            </ul>
          </div>
        </Space>
      </div>
    </div>
  );
};

export default Monitoring;
